import React from "react";
import QuickActions from "./QuickActions";
import { useAuth } from "@/hooks/useAuth";
import { isNannyUser } from "@/types/user";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import DashboardContainer from "@/components/layout/DashboardContainer";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface Employee {
  employeeId: number;
  firstName: string;
  lastName: string;
  hasActiveEnrollment: boolean;
  eligibility: {
    isPensionEligible: boolean;
    isAutoEnrollmentRequired: boolean;
  };
}

export default function NannyPensionActions() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [employee, setEmployee] = React.useState<Employee | null>(null);

  const employeeId = isNannyUser(user) ? user.employerEmployeeId : null;

  React.useEffect(() => {
    const load = async () => {
      if (!employeeId) {
        setError("No employee assigned to this user");
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        // Use the same endpoint as PensionsOverview to get employee data
        const employees = await ApiService.request<Employee[]>(
          EndpointKeys.FETCH_PENSIONS_OVERVIEW,
        );
        
        // Find the current employee in the list
        const currentEmployee = employees.find(emp => emp.employeeId === employeeId);
        
        if (currentEmployee) {
          setEmployee(currentEmployee);
        } else {
          setError("Could not find your pension information");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load pension information");
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [employeeId]);

  if (!employeeId) {
    return (
      <DashboardContainer title="Pension Actions">
        <Alert variant="destructive">
          <AlertDescription>
            We couldn't find your employee record. Please contact support.
          </AlertDescription>
        </Alert>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer title="My Pension">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {!error && employee && (
        <div className="max-w-2xl">
          {!isLoading && (
            <QuickActions
              employee={employee}
              isEmployeeView={true}
              onActionComplete={() => {
                // Refresh employee data after action
                const load = async () => {
                  try {
                    const employees = await ApiService.request<Employee[]>(
                      EndpointKeys.FETCH_PENSIONS_OVERVIEW,
                    );
                    const currentEmployee = employees.find(emp => emp.employeeId === employeeId);
                    if (currentEmployee) {
                      setEmployee(currentEmployee);
                    }
                  } catch (err) {
                    console.error("Failed to refresh employee data:", err);
                  }
                };
                load();
              }}
            />
          )}
        </div>
      )}
      {isLoading && (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
    </DashboardContainer>
  );
}


